import { useEffect, useMemo, useState } from 'react'
import { AxiosError } from 'axios'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import { ConfirmDialog } from '@components/common/ConfirmDialog'
import {
  useRankThresholds,
  useUpdateRankThresholds,
  useResetRankThresholds,
} from '../../hooks/useRankThresholds'

/**
 * The rank ladder: what each named tier costs in points.
 *
 * Deliberately only the numbers are editable. Tier names, badges and descriptions stay in code —
 * renaming Wayfarer is a copy change that wants review, whereas moving it from 1,000 to 1,400
 * points is a balance decision that wants trying against real data.
 *
 * Two things this screen has to communicate that the scoring panel does not:
 *  - a change is LIVE, with no recompute, because a rank is computed on read from a user's points;
 *  - it can therefore DEMOTE people. Badges are never affected — earned once, never removed — but
 *    the title on a profile moves the moment this is saved.
 */
export function RankLadderForm() {
  const { data, isLoading, error } = useRankThresholds()
  const update = useUpdateRankThresholds()
  const reset = useResetRankThresholds()

  // Local edits, keyed by tier. Seeded from the server and re-seeded whenever it answers.
  const [values, setValues] = useState<Record<number, string>>({})
  const [confirmReset, setConfirmReset] = useState(false)

  useEffect(() => {
    if (!data) return
    setValues(
      Object.fromEntries(
        data.tiers.filter((t) => t.isEditable).map((t) => [t.tier, String(t.pointsRequired)])
      )
    )
  }, [data])

  const editable = useMemo(() => data?.tiers.filter((t) => t.isEditable) ?? [], [data])

  const isDirty = useMemo(
    () => editable.some((t) => values[t.tier] !== undefined && Number(values[t.tier]) !== t.pointsRequired),
    [editable, values]
  )

  // Mirrors the server's rule so the reason is visible before the round trip, not after a 400.
  // The server still validates: this is a courtesy, not the gate.
  const ascendingError = useMemo(() => {
    let previous = 0
    let previousName = 'the first rank (0 points)'
    for (const tier of editable) {
      const raw = values[tier.tier]
      const n = Number(raw)
      if (raw === undefined || raw === '' || Number.isNaN(n)) return `${tier.name} needs a number.`
      if (!Number.isInteger(n)) return `${tier.name} must be a whole number of points.`
      if (data && (n < data.minThreshold || n > data.maxThreshold)) {
        return `${tier.name} must be between ${data.minThreshold} and ${data.maxThreshold.toLocaleString()} points.`
      }
      if (n <= previous) {
        return `${tier.name} must cost more than ${previousName} — ${n.toLocaleString()} does not come after ${previous.toLocaleString()}.`
      }
      previous = n
      previousName = tier.name
    }
    return null
  }, [editable, values, data])

  const saveError = update.error as AxiosError<{ message?: string }> | null
  const serverError = saveError?.response?.data?.message ?? (saveError ? 'Failed to save.' : null)

  const handleSubmit = () => {
    const thresholds: Record<number, number> = {}
    for (const tier of editable) thresholds[tier.tier] = Number(values[tier.tier])
    update.mutate({ thresholds })
  }

  const handleDiscard = () => {
    if (!data) return
    setValues(
      Object.fromEntries(
        data.tiers.filter((t) => t.isEditable).map((t) => [t.tier, String(t.pointsRequired)])
      )
    )
    update.reset()
  }

  if (isLoading) {
    return (
      <Paper sx={{ p: { xs: 2, sm: 3 }, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Paper>
    )
  }

  if (error || !data) {
    return <Alert severity="error">Failed to load the rank ladder. {(error as Error)?.message}</Alert>
  }

  const topTier = data.tiers[data.tiers.length - 1]

  return (
    <Paper sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Typography variant="h6" gutterBottom>
        Rank ladder
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        What each tier costs. Points come from completed achievements, so this ladder is measured
        against whatever those are currently worth.
      </Typography>

      <Alert severity="warning" sx={{ mb: 3 }}>
        <AlertTitle>Saving takes effect immediately</AlertTitle>
        A rank is worked out from a player&apos;s points every time their profile loads, so there is
        nothing to recompute — and raising a threshold above what somebody already holds will{' '}
        <strong>demote them</strong> on their next page load. Badges are never affected: once earned
        they are never removed.
      </Alert>

      {data.topTierUnreachable && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Nobody can reach {topTier?.name} yet</AlertTitle>
          Every active achievement together is worth{' '}
          <strong>{data.totalPointsAvailable.toLocaleString()}</strong> points, and the top tier
          costs {topTier?.pointsRequired.toLocaleString()}. That is fine if it is headroom for
          achievements you have not written yet — it is only a problem if you expected somebody to
          get there.
        </Alert>
      )}

      <Stack spacing={2} sx={{ mb: 3 }}>
        {data.tiers.map((tier) => {
          const isModified = tier.isEditable && Number(values[tier.tier]) !== tier.default
          return (
            // Wraps rather than overflows: this panel sits in a narrow column at md and is
            // the full width of a phone below that, and label + input + chips do not fit either.
            <Box key={tier.tier} sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ width: { xs: '100%', sm: 210 }, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography component="span" sx={{ fontSize: '1.25rem' }}>
                  {tier.badge}
                </Typography>
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    {tier.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Tier {tier.tier}
                  </Typography>
                </Box>
              </Box>

              {tier.isEditable ? (
                <>
                  <TextField
                    size="small"
                    type="number"
                    value={values[tier.tier] ?? ''}
                    onChange={(e) => setValues((v) => ({ ...v, [tier.tier]: e.target.value }))}
                    inputProps={{ min: data.minThreshold, max: data.maxThreshold, step: 1 }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">pts</InputAdornment>,
                    }}
                    sx={{ width: { xs: '100%', sm: 190 } }}
                    disabled={update.isPending || reset.isPending}
                  />
                  {isModified && <Chip size="small" label="Modified" color="warning" variant="outlined" />}
                  {isModified && (
                    <Tooltip title={`Back to the shipped default, ${tier.default.toLocaleString()} pts`}>
                      <Button
                        size="small"
                        startIcon={<RestartAltIcon />}
                        onClick={() => setValues((v) => ({ ...v, [tier.tier]: String(tier.default) }))}
                      >
                        Default
                      </Button>
                    </Tooltip>
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  0 pts — where everybody starts, so there is nothing to set.
                </Typography>
              )}
            </Box>
          )
        })}
      </Stack>

      {ascendingError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {ascendingError}
        </Alert>
      )}

      {serverError && !ascendingError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {serverError}
        </Alert>
      )}

      {update.isSuccess && !isDirty && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => update.reset()}>
          Rank ladder saved. It is in force now.
        </Alert>
      )}

      {/* useFlexGap so the row can wrap on a phone: Stack's default margin spacing leaves a
          stray gap on the wrapped line. */}
      <Stack direction="row" spacing={2} alignItems="center" useFlexGap flexWrap="wrap">
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isDirty || !!ascendingError || update.isPending || reset.isPending}
        >
          {update.isPending ? 'Saving…' : 'Save changes'}
        </Button>
        <Button variant="outlined" onClick={handleDiscard} disabled={!isDirty || update.isPending}>
          Discard changes
        </Button>
        <Tooltip title="Removes the stored values so the ladder tracks the shipped defaults again">
          <span>
            <Button
              color="inherit"
              onClick={() => setConfirmReset(true)}
              disabled={data.overrides.length === 0 || reset.isPending || update.isPending}
            >
              {reset.isPending ? 'Resetting…' : 'Reset all to defaults'}
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {data.overrides.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Rank thresholds changed from the defaults
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {data.overrides.length} tier{data.overrides.length === 1 ? '' : 's'} changed from the
            defaults.
          </Typography>
          {data.overrides.map((o) => (
            <Typography key={o.key} variant="caption" color="text.secondary" display="block">
              {o.key.replace('Rank.', '')} = {o.value} · {new Date(o.updatedAt).toLocaleString()}
              {o.updatedByDisplayName ? ` · ${o.updatedByDisplayName}` : ''}
            </Typography>
          ))}
        </Box>
      )}

      {/* The reset is a server call, not a form edit: it lands the moment it is confirmed. */}
      <ConfirmDialog
        open={confirmReset}
        title="Reset the rank ladder?"
        message={`This deletes the ${data.overrides.length} stored threshold${data.overrides.length === 1 ? '' : 's'} right now, with no Save step, and puts every tier back on its shipped default. Ranks are worked out live, so anyone whose points no longer reach their current tier is demoted on their next page load. Badges are not affected.`}
        confirmLabel="Reset ladder"
        destructive
        loading={reset.isPending}
        onConfirm={() => {
          setConfirmReset(false)
          reset.mutate()
        }}
        onClose={() => setConfirmReset(false)}
      />
    </Paper>
  )
}
